import { NextRouter } from 'next/router';
import { formatDate } from 'src/core/shared/dateUtils';
import { LOCALE } from '../../models/constants';
import { getCurrentBid } from '../../utils/getbid';
import { formatCurrency } from '../../utils/numberUtils';
import { Box } from '../box';
import { Flex } from '../flex';
import { Heading } from '../heading';
import { Text } from '../text';

const STRINGS = {
  it: {
    statusLabels: {
      coming_soon: 'Prezzo Richiesto',
      running: 'Offerta corrente',
      fallback: 'Compralo subito a',
      created: "Base d'asta",
      sold: 'Venduto in',
      sold_for_buy_now: 'Venduto in',
      closed_with_no_bids: 'Asta chiusa il',
      closed_with_winner_and_reserved_price_not_met: 'Asta chiusa il',
      closed_with_winner_and_reserved_price_met: 'Asta chiusa il',
      rent: 'Canone mensile richiesto',
      rented: 'Affittato in',
      reserved: 'Prezzo su richiesta',
      legalAuction: "Base d'asta",
    } as { [key: string]: string },
    secondaryLabel: {
      running: 'Offerte',
      created: 'Inizio',
    } as { [key: string]: string | null },
  },
  en: {
    statusLabels: {
      coming_soon: 'Price',
      running: 'Current bid',
      created: 'Starting bid',
      fallback: 'Buy now',
      sold: 'Sold in',
      sold_for_buy_now: 'Sold in',
      closed_with_no_bids: 'Closed on',
      closed_with_winner_and_reserved_price_not_met: 'Closed on',
      closed_with_winner_and_reserved_price_met: 'Closed on',
      rent: 'Monthly price asked',
      rented: 'Rented in',
      reserved: 'Price upon request',
      legalAuction: 'Starting Price',
    } as { [key: string]: string },
    secondaryLabel: {
      running: 'Bids',
      created: 'Auction starts',
    } as { [key: string]: string | null },
  },
};

const BUY_NOW_PRICE_STATUSES = [
  'buy_now_under_evaluation',
  'buy_now_only_under_evaluation',
  'under_evaluation',
  'only_buy_now',
  'coming_soon',
];

const CLOSED_STATUSES = [
  'closed_with_no_bids',
  'closed_with_winner_and_reserved_price_not_met',
  'closed_with_winner_and_reserved_price_met',
];

const SOLD_STATUSES = ['sold', 'sold_for_buy_now'];

export interface AuctionPriceProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  auction: any;
  locale: LOCALE;
  currencySymbol: string;
  contractType: string;
  propertyCode: string;
  reserved?: boolean;
  showOnlyPrice?: boolean;
  router?: NextRouter;
  widgetChangeViewBidList?: () => void;
}
export const AuctionPrice: React.FC<AuctionPriceProps> = (props) => {
  const key = props.auction.status.toString();
  let titleLabel = '';

  const isLegalAuction = ['PRE-AR', 'PRE-AC'].some((x) =>
    props.propertyCode.startsWith(x)
  );

  if (isLegalAuction) {
    titleLabel = STRINGS[props.locale].statusLabels['legalAuction'];
  } else if (props.contractType.toLowerCase() === 'affitto' && key === 'sold') {
    titleLabel = STRINGS[props.locale].statusLabels['rented'];
  } else if (props.reserved) {
    titleLabel = STRINGS[props.locale].statusLabels['reserved'];
  } else if (props.contractType.toLowerCase() === 'affitto') {
    titleLabel = STRINGS[props.locale].statusLabels['rent'];
  } else {
    titleLabel =
      STRINGS[props.locale].statusLabels[key] ||
      STRINGS[props.locale].statusLabels['fallback'];
  }

  const secondaryLabel: string | null =
    STRINGS[props.locale].secondaryLabel[key] || null;

  const valueByStatus = (): string => {
    if (BUY_NOW_PRICE_STATUSES.includes(props.auction.status)) {
      return formatCurrency(
        props.currencySymbol,
        props.auction.buyNowPrice.amount
      );
    }
    if (SOLD_STATUSES.includes(props.auction.status)) {
      return (
        getWeeksDiff() + `${props.locale === 'it' ? ' Settimane' : ' Weeks'}`
      );
    }
    if (CLOSED_STATUSES.includes(props.auction.status)) {
      return formatCurrency(
        props.currencySymbol,
        props.auction.buyNowPrice.amount
      );
    }
    return formatCurrency(props.currencySymbol, getCurrentBid(props.auction));
  };

  function getWeeksDiff(): number {
    const msInWeek = 1000 * 60 * 60 * 24 * 7;

    const elapsed = Math.abs(
      new Date(props.auction.startDate).getTime() -
        new Date(props.auction.updatedAt).getTime()
    );

    if (elapsed > msInWeek * 11) {
      return 12;
    }
    return msInWeek * 2 < elapsed ? Math.round(elapsed / msInWeek) : 2;
  }

  if (props.showOnlyPrice) {
    return !props.reserved ? (
      <Heading fontSize="5">{props.reserved ? '' : valueByStatus()}</Heading>
    ) : null;
  }

  return (
    <Box borderBottomRightRadius={'15px'}>
      <Flex justifyContent={'space-between'} alignItems={'start'}>
        <Box>
          <Text color="neutral500" fontSize="1">
            {titleLabel}
          </Text>
          <Heading as="div" fontSize="4" mt={2}>
            {props.reserved ? '‎' : valueByStatus()}
          </Heading>
        </Box>
        {secondaryLabel && (
          <Box>
            <Text color="neutral500" textAlign={'right'} fontSize="1">
              {secondaryLabel}
            </Text>
            {props.auction.status === 'running' ? (
              <div
                style={{
                  cursor: props.auction.bids.length > 0 ? 'pointer' : 'default',
                }}
              >
                <Heading
                  color={
                    props.auction.bids.length > 0 ? 'blueLight700' : 'black'
                  }
                  textAlign={'right'}
                  fontSize="2"
                  mt={4}
                  fontFamily="heading"
                  onClick={() => {
                    props.widgetChangeViewBidList
                      ? props.widgetChangeViewBidList()
                      : props.router &&
                        props.auction.bids.length > 0 &&
                        props.router.replace(
                          {
                            pathname: props.router.pathname,
                            query: {
                              ...props.router.query,
                              drawer: 'bidslist',
                            },
                          },
                          undefined,
                          { shallow: true }
                        );
                  }}
                >
                  {props.auction.bids.length}
                </Heading>
              </div>
            ) : (
              <Heading
                color="black"
                textAlign={'right'}
                fontSize="2"
                mt={4}
                fontFamily="heading"
              >
                {formatDate(props.auction.startDate)}
              </Heading>
            )}
          </Box>
        )}
      </Flex>
    </Box>
  );
};
